import { useState, useCallback } from "react";
import { apiService } from "../services/apiClient";
import { apiUrl } from "../services/apiUrl";

export const useCustomVisuals = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [visuals, setVisuals] = useState([]);
  const [customVisualsV2, setCustomVisualsV2] = useState([]);
  const [selectedVisuals, setSelectedVisuals] = useState([]);
  const [assignedGroups, setAssignedGroups] = useState([]);

  // Fetch the groups (personas) the caller is assigned to
  const fetchAssignedGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.aipdGetUserDetails());
      const set = response.data?.userGroupSet || {};
      const groups = Object.entries(set).map(([id, name]) => ({ id, name }));
      setAssignedGroups(groups);
      return groups;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching assigned groups:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch custom visuals
  const fetchCustomVisuals = useCallback(async (groupId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.aipdCustomVisuals(groupId));
      setVisuals(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching custom visuals:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch custom visuals V2 (with reportId/reportName/title/visualName)
  const fetchCustomVisualsV2 = useCallback(async (groupId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.aipdCustomVisualsV2(groupId));
      // API returns { all: [...], byGroup: [...] } — normalize to the flat list
      const list = Array.isArray(response.data) ? response.data : (response.data?.all ?? []);
      setCustomVisualsV2(list);
      return list;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching custom visuals V2:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch selected visuals
  const fetchSelectedVisuals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.aipdSelectedVisuals());
      setSelectedVisuals(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching selected visuals:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch selected visuals V2
  const fetchSelectedVisualsV2 = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.aipdSelectedVisualsV2());
      setSelectedVisuals(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching selected visuals V2:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Save the user's selected dashboard visuals (Customize Visuals form)
  const saveCustomVisualsV2 = useCallback(async (payload, groupId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(apiUrl.aipdSaveCustomVisuals(groupId), payload);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error saving custom visuals:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Persist a KPI selection event
  const saveKpiStats = useCallback(async (payload, groupId) => {
    try {
      const response = await apiService.post(apiUrl.aipdSaveKpiStats(groupId), payload);
      return response.data;
    } catch (err) {
      console.error("KPI stats error:", err.response?.data?.message || err.message);
      throw err;
    }
  }, []);

  // Fetch a single report's PowerBI embed config (used by visual preview/embed components)
  const fetchReportData = useCallback(async (reportId) => {
    try {
      const response = await apiService.get(apiUrl.aipdReportData(reportId));
      return response.data;
    } catch (err) {
      console.error("Error fetching report data:", err.response?.data?.message || err.message);
      throw err;
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Clear all visuals
  const clearAllVisuals = useCallback(() => {
    setVisuals([]);
    setCustomVisualsV2([]);
    setSelectedVisuals([]);
  }, []);

  return {
    loading,
    error,
    visuals,
    customVisualsV2,
    selectedVisuals,
    assignedGroups,
    fetchAssignedGroups,
    fetchCustomVisuals,
    fetchCustomVisualsV2,
    fetchSelectedVisuals,
    fetchSelectedVisualsV2,
    saveCustomVisualsV2,
    saveKpiStats,
    fetchReportData,
    clearError,
    clearAllVisuals,
  };
};

export default useCustomVisuals;
